import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, type CotacaoUberRetirada, type ObservacaoDeRetirada, type PedidoBuscado, type PedidoTiny, type Pickup } from "../api";
import { playFeedback } from "../feedback";
import { startScanning, type StopScanning } from "../scanner";

const brl = (cents: number) =>
  (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

/// A aba do setor 1: o pacote está pronto na bancada, com a etiqueta de envio
/// já colada, e precisa que o setor 2 venha buscar.
///
/// É deliberadamente separada da Bipagem principal: aquela lê a etiqueta da
/// PLATAFORMA (Mercado Livre, Shopee, Loja Integrada) no momento do despacho e
/// decide conta, transportadora e preço. Esta lê o código de rastreio já
/// impresso, só para avisar — sem interpretar formato nenhum, porque qualquer
/// código de rastreio serve de aviso.
///
/// O aviso chega ao setor 2 como notificação push no painel do computador. O
/// operador pode bipar vários pacotes seguidos antes do aviso sair — cada
/// bipagem é gravada na hora, mas o aviso só é enviado ao concluir, e junta
/// todos numa notificação só em vez de uma por etiqueta.

function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function Retirada() {
  const [aba, setAba] = useState<"bipar" | "pedidos">("bipar");
  const [hoje, setHoje] = useState<Pickup[]>([]);
  const [aguardando, setAguardando] = useState<PedidoTiny[]>([]);
  const [preparando, setPreparando] = useState<PedidoTiny[]>([]);
  const [camera, setCamera] = useState(false);
  const [cameraUber, setCameraUber] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  // A retirada sendo entregue ao cliente: bipar de novo e fotografar.
  const [entregando, setEntregando] = useState<Pickup | null>(null);

  // Sem alternar `carregando` — usada tanto na carga inicial (por baixo de
  // `recarregar`) quanto na atualização silenciosa em segundo plano, que não
  // pode fazer a lista sumir da tela por um instante a cada 15s.
  const carregarDados = useCallback(async () => {
    try {
      const [h, p] = await Promise.all([api.retiradasDeHoje(), api.pedidosTiny()]);
      setHoje(h.rows);
      setAguardando(p.aguardando);
      setPreparando(p.preparando);
    } catch {
      // A tela ainda serve para abrir a câmera sem as listas.
    }
  }, []);

  const recarregar = useCallback(async () => {
    setCarregando(true);
    await carregarDados();
    setCarregando(false);
  }, [carregarDados]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  // Atualiza sozinho enquanto a tela fica aberta — sem isto, um pedido que
  // muda de situação no Tiny (ex.: "aguardando" -> "em separação") só
  // aparecia quando a pessoa saísse da aba Pedidos e voltasse.
  useEffect(() => {
    const id = setInterval(() => void carregarDados(), 5000);
    return () => clearInterval(id);
  }, [carregarDados]);

  const abertos = hoje.filter((p) => !p.pickedUpAt).length;

  return (
    <main>
      <h2 className="section">Retirada</h2>

      <div className="choices">
        <button
          onClick={() => setAba("bipar")}
          style={
            aba === "bipar"
              ? { background: "var(--dep-lime)", borderColor: "var(--dep-lime)", color: "var(--dep-black)" }
              : undefined
          }
        >
          Bipar
        </button>
        <button
          onClick={() => setAba("pedidos")}
          style={
            aba === "pedidos"
              ? { background: "var(--dep-lime)", borderColor: "var(--dep-lime)", color: "var(--dep-black)" }
              : undefined
          }
        >
          Pedidos{aguardando.length > 0 && ` · ${aguardando.length}`}
        </button>
      </div>

      {aba === "bipar" ? (
        <>
          <p className="note">
            Bipe a etiqueta já colada no pacote pronto na bancada. Pode bipar vários seguidos —
            o aviso sai de uma vez só, ao concluir.
          </p>

          <div className="actions">
            <button className="btn primary" onClick={() => setCamera(true)}>
              Bipar etiqueta de retirada
            </button>
          </div>

          <p className="note">
            Pra retiradas que a própria casa decide entregar, em vez de esperar o cliente vir
            buscar — só pra pedidos específicos, não pra todos.
          </p>

          <div className="actions">
            <button className="btn" onClick={() => setCameraUber(true)}>
              Bipar e chamar Uber
            </button>
          </div>

          {erro && <p className="note erro">{erro}</p>}

          {!carregando && hoje.length > 0 && (
            <>
              <h2 className="section">
                Hoje · {hoje.length}
                {abertos > 0 && ` · ${abertos} aguardando`}
              </h2>
              <div className="batch">
                {hoje.map((p) => (
                  <div key={p.id} className="batch-item">
                    <div className="top">
                      <code>{p.code}</code>
                      <span
                        className={`pill ${p.pickedUpAt ? "ok" : p.acknowledgedAt ? "dup" : "pending"}`}
                      >
                        {p.pickedUpAt ? "retirado" : p.acknowledgedAt ? "avisado" : "pronto"}
                      </span>
                    </div>
                    {p.clienteNome && (
                      <div className="who">
                        <b>{p.clienteNome}</b>
                      </div>
                    )}
                    <div className="who">
                      bipado por {p.scannedBy} às {hora(p.createdAt)}
                      {p.acknowledgedAt &&
                        ` · bipado por ${p.acknowledgedBy} às ${hora(p.acknowledgedAt)}`}
                      {p.pickedUpAt && ` · cliente retirou às ${hora(p.pickedUpAt)}`}
                      {p.pickedUpAt && p.fotos > 0 && ` · ${p.fotos} ${p.fotos === 1 ? "foto" : "fotos"}`}
                    </div>
                    <ObservacaoDoPedido chave={p.chave} observacao={p.observacao} aoSalvar={carregarDados} />
                    {/* Etapa final: o cliente veio e levou. Não exige o aviso
                        confirmado antes — o mundo real acontece fora de ordem. */}
                    {!p.pickedUpAt && (
                      <div className="actions">
                        <button className="btn primary" onClick={() => setEntregando(p)}>
                          Bipar a entrega
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <BuscaPedidoTiny />

          <p className="note">
            Pedidos de retirada que já chegaram no Tiny e ainda precisam ser separados e
            bipados. Somem daqui assim que a etiqueta é lida — a partir daí o acompanhamento
            fica em "Hoje", na aba Bipar.
          </p>

          {/* As duas seções aparecem sempre, mesmo vazias — escondida quando
              vazia, "Pedidos sendo preparados" virava a primeira coisa
              visível sempre que não havia nenhum "aguardando" no momento, e
              parecia estar no lugar da outra seção. */}
          <h2 className="section">Pedidos aguardando separação</h2>
          {carregando ? null : aguardando.length === 0 ? (
            <p className="note">Nenhum pedido aguardando separação.</p>
          ) : (
            <div className="batch">
              {aguardando.map((p) => (
                <div key={p.numero} className="batch-item">
                  <div className="top">
                    <code>#{p.numero}</code>
                    <span className="pill pending">falta bipar</span>
                  </div>
                  <div className="who">
                    <b>{p.clienteNome || "cliente não identificado"}</b>
                  </div>
                  <ObservacaoDoPedido chave={p.chave} observacao={p.observacao} aoSalvar={carregarDados} />
                </div>
              ))}
            </div>
          )}

          <h2 className="section">Pedidos sendo preparados</h2>
          <p className="note">
            Já saíram de "aguardando separação" e estão sendo montados no Tiny. Ainda não é
            hora de bipar — aparecem aqui só para avisar que estão a caminho da bancada.
          </p>
          {carregando ? null : preparando.length === 0 ? (
            <p className="note">Nenhum pedido sendo preparado.</p>
          ) : (
            <div className="batch">
              {preparando.map((p) => (
                <div key={p.numero} className="batch-item">
                  <div className="top">
                    <code>#{p.numero}</code>
                    <span className="pill dup">em separação</span>
                  </div>
                  <div className="who">
                    <b>{p.clienteNome || "cliente não identificado"}</b>
                  </div>
                  <ObservacaoDoPedido chave={p.chave} observacao={p.observacao} aoSalvar={carregarDados} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {camera && (
        <LeitorRetirada
          onClose={() => {
            setCamera(false);
            void recarregar();
          }}
          onErro={setErro}
        />
      )}

      {cameraUber && (
        <LeitorUberRetirada
          onClose={() => {
            setCameraUber(false);
            void recarregar();
          }}
        />
      )}

      {entregando && (
        <EntregaRetirada
          pickup={entregando}
          onClose={(entregue) => {
            setEntregando(null);
            if (entregue) void recarregar();
          }}
        />
      )}
    </main>
  );
}

/// "097381" e "97381" são a mesma etiqueta (o zero da frente cai ao digitar).
const mesmoCodigo = (a: string, b: string) =>
  a.trim().replace(/^0+(?=\d)/, "").toUpperCase() === b.trim().replace(/^0+(?=\d)/, "").toUpperCase();

/// A entrega ao cliente, no lugar do antigo botão "Cliente retirou": bipar o
/// pacote de novo — tem que ser o desta retirada, o que evita fechar a do
/// cliente errado na correria do balcão. Bipou o certo, fecha na hora.
///
/// Até 12/10 pedia também uma foto da entrega; o Abner tirou essa etapa
/// ("agora é só bipar").
function EntregaRetirada({ pickup, onClose }: { pickup: Pickup; onClose: (entregue: boolean) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");
  const ocupado = useRef(false);
  // Por ref: a tela de fora recria `onClose` a cada atualização da lista
  // (de 5 em 5 segundos), e isso reiniciaria a câmera junto.
  const aoFechar = useRef(onClose);
  aoFechar.current = onClose;

  const conferir = useCallback(
    async (code: string) => {
      if (ocupado.current) return;
      if (!mesmoCodigo(code, pickup.code)) {
        playFeedback("UNIDENTIFIED");
        setErro(`Esse não é o pacote desta retirada (lido ${code}, esperado ${pickup.code}).`);
        return;
      }
      ocupado.current = true;
      setEnviando(true);
      setErro(null);
      try {
        await api.confirmarClienteRetirou(pickup.id, code, []);
        playFeedback("OK");
        aoFechar.current(true);
      } catch (err) {
        playFeedback("UNIDENTIFIED");
        setErro(err instanceof Error ? err.message : "Não foi possível confirmar a retirada.");
        setEnviando(false);
        ocupado.current = false;
      }
    },
    [pickup.id, pickup.code]
  );

  useEffect(() => {
    let stop: StopScanning | undefined;
    let cancelado = false;

    void (async () => {
      const video = videoRef.current;
      if (!video) return;
      const fn = await startScanning(video, (code) => void conferir(code), setErro);
      if (cancelado) fn();
      else stop = fn;
    })();

    return () => {
      cancelado = true;
      stop?.();
    };
  }, [conferir]);

  return createPortal(
    <div className="scan-screen">
      <video ref={videoRef} muted playsInline />

      <div className="viewfinder barras" aria-hidden="true">
        <span className="c tl" />
        <span className="c tr" />
        <span className="c bl" />
        <span className="c br" />
      </div>

      <button className="scan-back" onClick={() => onClose(false)} aria-label="Voltar sem confirmar">
        ←
      </button>

      <div className="scan-hint">
        Bipe o pacote que o cliente vai levar
        <br />
        <small>
          {pickup.code}
          {pickup.clienteNome ? ` · ${pickup.clienteNome}` : ""}
        </small>
      </div>

      {enviando && <div className="scan-warn">Confirmando a entrega…</div>}
      {erro && <div className="scan-warn erro">{erro}</div>}

      {typing ? (
        <div className="typing">
          <input
            autoFocus
            value={typed}
            placeholder="Código da etiqueta"
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && typed.trim() && void conferir(typed.trim())}
          />
          <button className="btn-deliver" disabled={enviando} onClick={() => typed.trim() && void conferir(typed.trim())}>
            Conferir
          </button>
          <button className="btn-ghost" onClick={() => setTyping(false)}>
            Cancelar
          </button>
        </div>
      ) : (
        <div className="sheet">
          <div className="sheet-actions">
            <button className="btn-ghost" onClick={() => setTyping(true)}>
              Digitar
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

/// Câmera de tela cheia dedicada à etiqueta de retirada.
///
/// Separada da câmera de bipagem principal de propósito: aqui não há conta,
/// transportadora nem preço a resolver — é só ler o código. Misturar as duas
/// transformaria um pacote pronto na bancada num pacote despachado, que é
/// outro evento inteiramente.
///
/// Bipagem contínua: cada leitura é gravada na hora (silenciosa, sem avisar
/// ninguém), e some da fila de "Pedidos" imediatamente. O aviso de verdade —
/// UMA notificação, com a lista de tudo — só sai quando o operador conclui.
function LeitorRetirada({
  onClose,
  onErro,
}: {
  onClose: () => void;
  onErro: (mensagem: string | null) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lote, setLote] = useState<Pickup[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const [aberto, setAberto] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");

  const ocupado = useRef(false);
  const loteRef = useRef<Pickup[]>([]);
  loteRef.current = lote;

  const registrar = useCallback(async (code: string) => {
    if (ocupado.current) return;
    ocupado.current = true;
    setEnviando(true);
    setErro(null);

    try {
      const r = await api.registrarRetirada(code, true);
      playFeedback(r.jaAberta ? "DUPLICATE" : "OK");

      // Mesmo pacote bipado de novo no MESMO lote: não duplica na lista.
      setLote((prev) => (prev.some((p) => p.id === r.pickup.id) ? prev : [r.pickup, ...prev]));
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível registrar a retirada.");
    } finally {
      setEnviando(false);
      ocupado.current = false;
    }
  }, []);

  useEffect(() => {
    let stop: StopScanning | undefined;
    let cancelado = false;

    void (async () => {
      const video = videoRef.current;
      if (!video) return;
      const fn = await startScanning(video, (code) => void registrar(code), setErro);
      if (cancelado) fn();
      else stop = fn;
    })();

    return () => {
      cancelado = true;
      stop?.();
    };
  }, [registrar]);

  const enviarDigitado = async () => {
    const code = typed.trim();
    if (!code) return;
    setTyped("");
    setTyping(false);
    await registrar(code);
  };

  /// Manda o aviso do lote inteiro e fecha. Chamado tanto pelo botão
  /// "Concluir" quanto pela seta de voltar — sair da câmera com pacotes
  /// bipados e sem avisar ninguém não pode ser um jeito de esquecer o aviso.
  const concluir = async () => {
    const ids = loteRef.current.map((p) => p.id);

    if (ids.length > 0) {
      setConcluindo(true);
      try {
        await api.avisarLote(ids);
      } catch (err) {
        // Os pacotes já estão gravados — só o aviso falhou. Deixa a tela
        // dizer isso em vez de sumir como se tivesse dado certo.
        onErro(err instanceof Error ? err.message : "Pacotes gravados, mas o aviso falhou.");
        setConcluindo(false);
        return;
      }
      setConcluindo(false);
    }

    onErro(null);
    onClose();
  };

  const total = lote.length;

  // Direto no <body>: esta câmera é chamada de dentro do <main> da tela de
  // Retirada, que rola (`overflow-y: auto`). No Safari do iPhone, um
  // `position: fixed` preso dentro de um contêiner que rola fica cortado
  // pelos limites daquele contêiner em vez de cobrir a tela toda — o menu de
  // baixo aparecia por cima da câmera. O portal tira a árvore de onde ela foi
  // chamada e bota no <body>, imune ao aninhamento.
  return createPortal(
    <div className="scan-screen">
      <video ref={videoRef} muted playsInline />

      <div className="viewfinder barras" aria-hidden="true">
        <span className="c tl" />
        <span className="c tr" />
        <span className="c bl" />
        <span className="c br" />
      </div>

      <button className="scan-back" onClick={() => void concluir()} aria-label="Voltar e concluir">
        ←
      </button>

      <div className="scan-hint">Leia a etiqueta do pacote</div>

      {enviando && <div className="scan-warn">Registrando…</div>}
      {erro && <div className="scan-warn erro">{erro}</div>}

      {typing && (
        <div className="typing">
          <input
            autoFocus
            value={typed}
            placeholder="Código da etiqueta"
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void enviarDigitado()}
          />
          <button className="btn-deliver" onClick={() => void enviarDigitado()}>
            Conferir
          </button>
          <button className="btn-ghost" onClick={() => setTyping(false)}>
            Cancelar
          </button>
        </div>
      )}

      {!typing && (
        <div className={`sheet${aberto ? " open" : ""}`}>
          <button
            className="grabber"
            onClick={() => setAberto((v) => !v)}
            aria-expanded={aberto}
            aria-label={aberto ? "Recolher lista" : "Ver os pacotes bipados"}
          >
            <span />
          </button>

          {!aberto ? (
            <div className="sheet-head">
              <div className="count">
                {total} {total === 1 ? "pacote" : "pacotes"}
              </div>
              <div className="sub">
                {total === 0 ? "Aponte a câmera para a etiqueta." : "Deslize para cima para ver a lista."}
              </div>
            </div>
          ) : (
            <div className="sheet-list">
              <div className="sheet-head compact">
                <div className="count">
                  {total} {total === 1 ? "pacote" : "pacotes"}
                </div>
              </div>

              {total === 0 ? (
                <p className="sheet-empty">Nada bipado ainda.</p>
              ) : (
                lote.map((p) => (
                  <div key={p.id} className="sheet-item">
                    <div className="top">
                      <code>{p.code}</code>
                      <button
                        className="remove"
                        onClick={() => setLote((prev) => prev.filter((x) => x.id !== p.id))}
                        aria-label="Tirar do aviso"
                      >
                        Tirar
                      </button>
                    </div>
                    {p.clienteNome && <div className="meta">{p.clienteNome}</div>}
                  </div>
                ))
              )}
            </div>
          )}

          <div className="sheet-actions">
            <button className="btn-ghost" onClick={() => setTyping(true)}>
              Digitar
            </button>
            <button className="btn-deliver" disabled={concluindo} onClick={() => void concluir()}>
              {concluindo ? "Concluindo…" : `Concluir${total > 0 ? ` · ${total}` : ""}`}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

/// Câmera de UM código só, pra chamar o Uber numa retirada específica — a
/// própria casa decide entregar, em vez de esperar o cliente vir buscar.
/// Separada de `LeitorRetirada` de propósito: aquela bipa em lote e nunca
/// gasta dinheiro nenhum; esta é uma ação só, com pausa pra confirmar antes
/// de custar de verdade.
///
/// Duas etapas, como no painel: cotar (`/pickups/uber/cotar`) é de graça e
/// só mostra endereço e preço; confirmar (`/pickups/uber/confirmar`) é o
/// toque que desloca o motorista e cobra.
function LeitorUberRetirada({ onClose }: { onClose: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [etapa, setEtapa] = useState<"scanning" | "cotando" | "cotacao" | "confirmando" | "sucesso">(
    "scanning"
  );
  const [code, setCode] = useState("");
  const [cotacao, setCotacao] = useState<CotacaoUberRetirada | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sucessoLink, setSucessoLink] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState("");

  const ocupado = useRef(false);

  const cotar = useCallback(async (codigo: string) => {
    if (ocupado.current) return;
    ocupado.current = true;
    setCode(codigo);
    setErro(null);
    setEtapa("cotando");

    try {
      const r = await api.cotarUberRetirada(codigo);
      playFeedback("OK");
      setCotacao(r);
      setEtapa("cotacao");
    } catch (err) {
      playFeedback("UNIDENTIFIED");
      setErro(err instanceof Error ? err.message : "Não foi possível cotar o Uber.");
      setEtapa("scanning");
    } finally {
      ocupado.current = false;
    }
  }, []);

  useEffect(() => {
    if (etapa !== "scanning") return;

    let stop: StopScanning | undefined;
    let cancelado = false;

    void (async () => {
      const video = videoRef.current;
      if (!video) return;
      const fn = await startScanning(video, (codigo) => void cotar(codigo), setErro);
      if (cancelado) fn();
      else stop = fn;
    })();

    return () => {
      cancelado = true;
      stop?.();
    };
  }, [etapa, cotar]);

  const enviarDigitado = async () => {
    const codigo = typed.trim();
    if (!codigo) return;
    setTyped("");
    setTyping(false);
    await cotar(codigo);
  };

  const cancelarCotacao = () => {
    setCotacao(null);
    setCode("");
    setErro(null);
    setEtapa("scanning");
  };

  const confirmar = async () => {
    if (!cotacao) return;
    setEtapa("confirmando");
    setErro(null);
    try {
      const r = await api.confirmarUberRetirada(code, cotacao.quoteId, cotacao.endereco, cotacao.pesoGramas);
      setSucessoLink(r.link);
      setEtapa("sucesso");
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível chamar o Uber.");
      setEtapa("cotacao");
    }
  };

  return createPortal(
    <div className="scan-screen">
      {etapa === "scanning" && <video ref={videoRef} muted playsInline />}

      {etapa === "scanning" && (
        <div className="viewfinder barras" aria-hidden="true">
          <span className="c tl" />
          <span className="c tr" />
          <span className="c bl" />
          <span className="c br" />
        </div>
      )}

      <button className="scan-back" onClick={onClose} aria-label="Fechar">
        ←
      </button>

      {etapa === "scanning" && <div className="scan-hint">Leia a etiqueta da retirada</div>}
      {etapa === "cotando" && <div className="scan-hint">Cotando o Uber…</div>}

      {erro && <div className="scan-warn erro">{erro}</div>}

      {etapa === "scanning" && typing && (
        <div className="typing">
          <input
            autoFocus
            value={typed}
            placeholder="Código da etiqueta"
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void enviarDigitado()}
          />
          <button className="btn-deliver" onClick={() => void enviarDigitado()}>
            Conferir
          </button>
          <button className="btn-ghost" onClick={() => setTyping(false)}>
            Cancelar
          </button>
        </div>
      )}

      {etapa === "scanning" && !typing && (
        <div className="sheet open">
          <div className="sheet-list">
            <div className="sheet-head">
              <div className="sub">Aponte a câmera pra etiqueta, ou digite o código.</div>
            </div>
          </div>
          <div className="sheet-actions">
            <button className="btn-ghost" onClick={() => setTyping(true)}>
              Digitar
            </button>
          </div>
        </div>
      )}

      {(etapa === "cotacao" || etapa === "confirmando") && cotacao && (
        <div className="sheet open">
          <div className="sheet-list">
            <div className="sheet-head">
              <div className="count">{brl(cotacao.feeCents)}</div>
              <div className="sub">
                {cotacao.etaMinutos !== null ? `Chega em ~${cotacao.etaMinutos} min` : "Uber Direct"}
              </div>
            </div>
            <div className="sheet-item">
              <div className="top">
                <b>{cotacao.endereco.nomeContato}</b>
              </div>
              <div className="meta">
                {cotacao.endereco.telefoneContato && <>{cotacao.endereco.telefoneContato}<br /></>}
                {cotacao.endereco.endereco}, {cotacao.endereco.numero}
                {cotacao.endereco.complemento ? ` — ${cotacao.endereco.complemento}` : ""}
                <br />
                {cotacao.endereco.bairro}, {cotacao.endereco.municipio}/{cotacao.endereco.uf}
                <br />
                CEP {cotacao.endereco.cep}
              </div>
            </div>
            <div className="sheet-item">
              <div className="top">
                <b>Peso informado à Uber</b>
              </div>
              <div className="meta">
                {cotacao.pesoGramas !== null
                  ? `${(cotacao.pesoGramas / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 2 })} kg — ajuda a evitar que mandem moto pra um pedido pesado`
                  : "Não foi possível calcular — a Uber vai decidir o entregador só pela disponibilidade"}
              </div>
            </div>
          </div>
          <div className="sheet-actions">
            <button className="btn-ghost" disabled={etapa === "confirmando"} onClick={cancelarCotacao}>
              Cancelar
            </button>
            <button className="btn-deliver" disabled={etapa === "confirmando"} onClick={() => void confirmar()}>
              {etapa === "confirmando" ? "Chamando…" : "Confirmar e chamar Uber"}
            </button>
          </div>
        </div>
      )}

      {etapa === "sucesso" && (
        <div className="sheet open">
          <div className="sheet-list">
            <div className="sheet-head">
              <div className="count">Uber chamado</div>
              <div className="sub">A corrida já está a caminho.</div>
            </div>
          </div>
          <div className="sheet-actions">
            {sucessoLink && (
              <a className="btn-ghost" href={sucessoLink} target="_blank" rel="noreferrer">
                Ver corrida
              </a>
            )}
            <button className="btn-deliver" onClick={onClose}>
              Concluir
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

/// O recado de um pedido — "cliente vem depois das 17h", "falta o brinde".
/// Pedido do Abner em 09/10: o Tiny não tem onde mostrar isso pra bancada,
/// então fica no nosso sistema. É o MESMO recado do painel (mesma chave): o
/// que se escreve num aparece no outro.
function ObservacaoDoPedido({
  chave,
  observacao,
  aoSalvar,
  pelaBusca = false,
}: {
  chave: string;
  observacao: ObservacaoDeRetirada | null;
  aoSalvar: () => Promise<void> | void;
  /// Escrita pela busca — é o que põe o pedido em "Pedidos com observação".
  pelaBusca?: boolean;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const atual = observacao?.texto ?? "";

  const abrir = () => {
    setTexto(atual);
    setErro(null);
    setEditando(true);
  };

  const salvar = async (novo: string) => {
    setSalvando(true);
    setErro(null);
    try {
      await api.salvarObservacaoDeRetirada(chave, novo.trim(), pelaBusca);
      await aoSalvar();
      setEditando(false);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível salvar a observação.");
    } finally {
      setSalvando(false);
    }
  };

  if (editando) {
    return (
      <div className="observacao-pedido editando">
        <textarea
          autoFocus
          rows={2}
          maxLength={280}
          placeholder="Ex.: cliente vem depois das 17h"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
        {erro && <p className="note erro">{erro}</p>}
        <div className="actions">
          <button className="btn primary" disabled={salvando} onClick={() => void salvar(texto)}>
            {salvando ? "Salvando…" : "Salvar"}
          </button>
          {atual && (
            <button className="btn" disabled={salvando} onClick={() => void salvar("")}>
              Apagar
            </button>
          )}
          <button className="btn" disabled={salvando} onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="observacao-pedido">
      {atual && (
        <div className="recado" title={observacao?.autor ? `por ${observacao.autor}` : undefined}>
          📝 {atual}
          {observacao?.autor && <small> — {observacao.autor}</small>}
        </div>
      )}
      <button className="link-btn" onClick={abrir}>
        {atual ? "Editar observação" : "+ Observação"}
      </button>
    </div>
  );
}

/// Acha QUALQUER pedido do Tiny — não só retirada — pelo nome do cliente
/// (o do Tiny ou o da pessoa na Loja Integrada) ou pelo número, pra colocar
/// uma observação. Pedido do Abner em 09/10. O recado fica no nosso sistema
/// e aparece também em Retiradas e Avulso, que usam a mesma chave.
///
/// Embaixo da busca, sempre à vista, os pedidos que já têm observação — sem
/// isso, o recado posto num pedido que não é de retirada sumia da tela ao
/// limpar a busca.
function BuscaPedidoTiny() {
  const [q, setQ] = useState("");
  const [resultado, setResultado] = useState<PedidoBuscado[] | null>(null);
  const [comObservacao, setComObservacao] = useState<PedidoBuscado[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregarComObservacao = useCallback(async () => {
    try {
      setComObservacao((await api.pedidosComObservacao()).pedidos);
    } catch {
      // A busca continua servindo sem a lista.
    }
  }, []);

  useEffect(() => {
    void carregarComObservacao();
  }, [carregarComObservacao]);

  const buscar = useCallback(async (termo: string) => {
    if (termo.trim().length < 2) return;
    setBuscando(true);
    setErro(null);
    try {
      setResultado((await api.buscarPedidoTiny(termo.trim())).pedidos);
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Não foi possível buscar.");
    } finally {
      setBuscando(false);
    }
  }, []);

  const depoisDeSalvar = async () => {
    await Promise.all([carregarComObservacao(), q.trim().length >= 2 ? buscar(q) : Promise.resolve()]);
  };

  const cartao = (p: PedidoBuscado) => (
    <div key={p.numero} className="batch-item">
      <div className="top">
        <code>#{p.numero}</code>
        <span className="pill dup">{p.situacao}</span>
      </div>
      <div className="who">
        <b>{p.clienteNome}</b>
      </div>
      <div className="who">
        {[p.formaEnvio, new Date(p.criadoEm).toLocaleDateString("pt-BR")].filter(Boolean).join(" · ")}
      </div>
      <ObservacaoDoPedido chave={p.chave} observacao={p.observacao} aoSalvar={depoisDeSalvar} pelaBusca />
    </div>
  );

  return (
    <>
      <h2 className="section">Observação em qualquer pedido</h2>
      <div className="busca-pedido">
        <input
          type="search"
          placeholder="Nome do cliente ou número do pedido"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            if (!e.target.value.trim()) setResultado(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && void buscar(q)}
        />
        <button className="btn primary" disabled={buscando || q.trim().length < 2} onClick={() => void buscar(q)}>
          {buscando ? "Buscando…" : "Buscar"}
        </button>
      </div>
      {erro && <p className="note erro">{erro}</p>}

      {resultado !== null &&
        (resultado.length === 0 ? (
          <p className="note">Nenhum pedido encontrado no Tiny.</p>
        ) : (
          <div className="batch">{resultado.map(cartao)}</div>
        ))}

      <h2 className="section">Pedidos com observação · {comObservacao.length}</h2>
      {comObservacao.length === 0 ? (
        <p className="note">Nenhum pedido em andamento com observação posta pela busca.</p>
      ) : (
        <div className="batch">{comObservacao.map(cartao)}</div>
      )}
    </>
  );
}
